import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {Table, Button} from 'antd';
import {SwapRightOutlined, DoubleLeftOutlined, SwapLeftOutlined} from '@ant-design/icons';
import {useOutletContext} from 'react-router-dom';
import {
    currencyNoSymbolFormatter,
    extractStringDateBySecond,
} from '../../utils';
import OrdersColumns from './orders.constants';
import {useTranslation} from 'react-i18next';
import {APIv1} from '../../api';
import useUserToken from '../../hooks/user/useUserToken';
import {transformDistricts} from "./orderUtils/generateOrderType";
import {subtractDates} from "../../utils/date/dateUtils";
import {DefinePaymentType} from "../../utils/orderUtils";
import useUrlTableState from "../../hooks/useUrlTableState";
import ListStateIndicator from "../../components/ListStateIndicator/ListStateIndicator";

const ORDERS_FILTER_PARAM_MAP = {
    market_name: 'market__id',
    payment_type: 'payment_type',
};

const ORDERS_FILTER_LABELS = {
    market__id: 'Market',
    payment_type: 'Payment',
};

const Orders = ({serialNumber = null, orderType = null}) => {
    const {t} = useTranslation();
    const {searchText} = useOutletContext();
    const [userData, setUserData] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    });
    const [ordersData, setOrdersData] = useState([]);
    const [districtsData, setDistrictsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cursorUrl, setCursorUrl] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [prevCursor, setPrevCursor] = useState(null);
    const [hideOrderNumber, setHideOrderNumber] = useState(false);  // to hide orderNumber and replace with order ID
    const tableState = useUrlTableState({
        filterParamMap: ORDERS_FILTER_PARAM_MAP,
        filterLabels: ORDERS_FILTER_LABELS,
        searchText,
        includePagination: false,
    });

    const marketOptions = districtsData.map(d => ({
        label: d.name,   // District name
        title: d.name,
        options: d.market.map(m => ({
            label: m.name,  // What user sees (Cyrillic name)
            value: m.id     // What is sent to the server
        }))
    }));

    const token = useUserToken(userData.token);
    const columns = useMemo(() => OrdersColumns(t, hideOrderNumber, marketOptions, tableState.filters), [t, hideOrderNumber, marketOptions, tableState.filters]);
    const controlledColumns = tableState.controlledColumns(columns);
    // After fetching districts from API
    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const response = await APIv1.get('/market/list/', {
                    headers: {Authorization: `Token ${token}`},
                });

                const transformed = transformDistricts(response.data.results || []);
                setDistrictsData(transformed);
            } catch (err) {
                console.error(err);
                setDistrictsData([]);
            }
        };

        if (token) fetchDistricts();
    }, [token]);


    const fetchOrdersData = useCallback(async () => {
        if (!token) return;
        setLoading(true);

        try {
            const baseUrl = serialNumber === null
                ? `/companies/orders/list?type=${orderType}`
                : `/orders/list/?serial=${serialNumber}`;

            const url = cursorUrl || baseUrl;

            const params = cursorUrl ? {} : {
                ordering: tableState.ordering,
                search: tableState.search,
                ...tableState.filters,
            };
            Object.keys(params).forEach(k => {
                if (params[k] === null || params[k] === undefined) {
                    delete params[k];
                }
            });
            const response = await APIv1.get(url, {
                params,
                headers: {Authorization: `Token ${token}`},
            });

            // Check if any orders have result_url and set hideNumber accordingly
            const hasResultUrl = response.data.results.some(report => report.result_url);

            // Set hideNumber state
            setHideOrderNumber(!hasResultUrl);

            const data = response.data.results.map(report => ({
                key: report.id,
                number: report.number_result_url ?? report.id,
                market_name: report.market_name ?? '-',
                cashier: report.cashier ?? '-',
                cash_desc_serial: report.cash_desc_serial,
                orderPrice: currencyNoSymbolFormatter(report.order_price / 100),
                payment_type: DefinePaymentType(report.type, report.payment_type, (report.order_price ?? 0) / 100),
                type: report.type,
                time: report.time
                    ? extractStringDateBySecond(report.time)
                    : '----/--/--',
                created_date: report.created_date
                    ? extractStringDateBySecond(report.created_date)
                    : '----/--/--',
                order_processing_time: subtractDates(new Date(report.created_date), new Date(report.time)),
            }));

            setOrdersData(data);
            setNextCursor(response.data.next);
            setPrevCursor(response.data.previous);
        } catch (err) {
            console.error('Failed to fetch orders:', err);
        } finally {
            setLoading(false);
        }
    }, [token, cursorUrl, tableState.ordering, tableState.search, tableState.filters, serialNumber, orderType]);

    useEffect(() => {
        fetchOrdersData();
    }, [fetchOrdersData, tableState.filters, tableState.ordering, tableState.search, cursorUrl]);

    useEffect(() => {
        setCursorUrl(null); // Reset to first page on filter/sort/search change
    }, [tableState.search, tableState.ordering, tableState.filters]);

    const handleTableChangeWrapper = useCallback((pagination, filtersFromTable, sorter, extra) => {
        tableState.handleTableChange(columns)(pagination, filtersFromTable, sorter, extra);
        setCursorUrl(null);  // reset page on filter change
    }, [columns, tableState]);

    return (
        <div className="content_container">
            {/*<div style={{marginBottom: 15, display: 'flex', justifyContent: 'flex-end', gap: 15}}>*/}
            {/*    <ExportExcelButton*/}
            {/*        t={t}*/}
            {/*        userData={userData}*/}
            {/*        marketOptions={marketOptions}*/}
            {/*        exportUrl="order/check/excel/export/"*/}
            {/*        dateParamNames={{start: 'begin_date', end: 'end_date'}}*/}
            {/*        buttonLabel={t('pages.orders.listColumns.importExcel1')}*/}
            {/*        fileExtension="xlsx"*/}
            {/*        dateFormat="YYYY-MM-DD HH:mm:ss"*/}
            {/*        prefixType="Checks"*/}
            {/*        orderType={orderType}*/}
            {/*        includeInn*/}
            {/*    />*/}
            {/*</div>*/}
            <ListStateIndicator items={tableState.stateIndicators} onReset={tableState.resetState} onRemove={tableState.removeState}/>

            <Table
                columns={controlledColumns}
                dataSource={ordersData}
                loading={loading}
                virtual
                onChange={handleTableChangeWrapper}
                pagination={false}
            />

            <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 16, gap: 8}}>
                <div style={{display: 'flex', gap: 8}}>
                    <Button
                        type="primary"
                        shape="round"
                        icon={<DoubleLeftOutlined/>}
                        size="medium"
                        disabled={cursorUrl === null}
                        onClick={() => setCursorUrl(null)}
                    >
                        {t('pages.orders.listColumns.button1')}
                    </Button>
                    <Button
                        type="primary"
                        shape="round"
                        icon={<SwapLeftOutlined/>}
                        size="medium"
                        disabled={!prevCursor}
                        onClick={() => setCursorUrl(prevCursor)}
                    >
                        {t('pages.orders.listColumns.button2')}
                    </Button>
                </div>
                <Button
                    type="primary"
                    shape="round"
                    size="medium"
                    disabled={!nextCursor}
                    onClick={() => setCursorUrl(nextCursor)}
                >
                    <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                      {t('pages.orders.listColumns.button3')}
                        <SwapRightOutlined/>
                    </span>
                </Button>
            </div>
        </div>
    );
};

export default Orders;
